/**
 * /pay/<slug> — PUBLIC pay-by-invoice entry. The customer types an invoice number; we look it up
 * (org resolved from the slug) and route to the invoice detail to pay. Branded with the business
 * name. No auth, no card data here. NEVER imports @smartbox/*.
 */
import { useState } from 'react';
import { ActivityIndicator, Image, StyleSheet, View } from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useQuery } from '@tanstack/react-query';
import { getPublicBrand } from '../../../../src/api/publicPay';
import { Screen, Text, Card, Button, Field } from '../../../../src/components/ui';
import { theme } from '../../../../src/theme';
import { extractErrorMessage } from '../../../../src/lib/format';

export default function PaySlugScreen() {
  const router = useRouter();
  const { slug } = useLocalSearchParams<{ slug: string }>();
  const [number, setNumber] = useState('');
  const [touched, setTouched] = useState(false);

  const brand = useQuery({
    queryKey: ['public-brand', slug],
    queryFn: () => getPublicBrand(slug!),
    enabled: !!slug,
    retry: false,
    staleTime: 60_000,
  });

  if (brand.isLoading) {
    return (
      <Screen>
        <View style={styles.center}>
          <ActivityIndicator color={theme.color.action.primary} accessibilityLabel="Loading" />
        </View>
      </Screen>
    );
  }

  if (brand.isError || !brand.data) {
    return (
      <Screen>
        <Card>
          <Text variant="bodyStrong" color="danger" accessibilityRole="alert">
            {extractErrorMessage(brand.error, 'This pay page was not found.')}
          </Text>
        </Card>
      </Screen>
    );
  }

  const canPay = brand.data.acceptsPayments;
  const accent = brand.data.accentColor;

  return (
    <Screen scroll>
      {brand.data.logoUrl ? (
        <Image
          source={{ uri: brand.data.logoUrl }}
          style={styles.logo}
          resizeMode="contain"
          accessibilityLabel={`${brand.data.businessName} logo`}
        />
      ) : null}
      <Text variant="title">{brand.data.businessName}</Text>
      <Text variant="body" color="secondary">Pay an invoice online.</Text>

      {!canPay ? (
        <Card>
          <Text variant="body" color="secondary" accessibilityRole="alert">
            This business isn't accepting online payments right now. Please contact them directly.
          </Text>
        </Card>
      ) : (
        <Card>
          <Field
            label="Invoice number"
            placeholder="e.g. 1042"
            value={number}
            onChangeText={(t) => {
              setNumber(t);
              setTouched(true);
            }}
            autoCapitalize="none"
            autoCorrect={false}
            returnKeyType="go"
            onSubmitEditing={() => {
              if (number.trim()) router.push(`/pay/${slug}/${encodeURIComponent(number.trim())}`);
            }}
            accessibilityLabel="Invoice number"
          />
          {touched && !number.trim() ? (
            <Text variant="small" color="secondary">Enter the invoice number from your invoice.</Text>
          ) : null}
          <Button
            title="Find invoice"
            disabled={!number.trim()}
            onPress={() => router.push(`/pay/${slug}/${encodeURIComponent(number.trim())}`)}
            accessibilityLabel="Find invoice"
            style={number.trim() ? { backgroundColor: accent } : undefined}
          />
        </Card>
      )}
    </Screen>
  );
}

const styles = StyleSheet.create({
  center: { paddingVertical: theme.spacing.lg, alignItems: 'center' },
  logo: { height: 56, width: 200, alignSelf: 'flex-start', marginBottom: theme.spacing.sm },
});
